// 图片缓存管理器
class ImageCacheManager {
  constructor() {
    this.cache = new Map();
    this.loadingPromises = new Map();
    this.maxCacheSize = 100; // 最大缓存数量
    this.cacheStats = {
      hits: 0,
      misses: 0,
      totalRequests: 0
    };
  }

  // 生成缓存键
  getCacheKey(url) {
    return url;
  }

  // 检查缓存
  has(url) {
    return this.cache.has(this.getCacheKey(url));
  }

  // 获取缓存
  get(url) {
    const key = this.getCacheKey(url);
    this.cacheStats.totalRequests++;
    
    if (this.cache.has(key)) {
      this.cacheStats.hits++;
      const item = this.cache.get(key);
      // 更新访问时间
      item.lastAccessed = Date.now();
      return item.blob;
    }
    
    this.cacheStats.misses++;
    return null;
  }

  // 设置缓存
  set(url, blob) {
    const key = this.getCacheKey(url);
    
    // 如果缓存已满，删除最久未访问的项
    if (this.cache.size >= this.maxCacheSize) {
      this.evictLeastRecentlyUsed();
    }

    this.cache.set(key, {
      blob,
      createdAt: Date.now(),
      lastAccessed: Date.now(),
      size: blob.size || 0
    });
  }

  // 删除最久未访问的缓存项
  evictLeastRecentlyUsed() {
    let oldestKey = null;
    let oldestTime = Date.now();

    for (const [key, item] of this.cache.entries()) {
      if (item.lastAccessed < oldestTime) {
        oldestTime = item.lastAccessed;
        oldestKey = key;
      }
    }

    if (oldestKey) {
      this.cache.delete(oldestKey);
    }
  }

  // 预加载图片
  async preload(url, options = {}) {
    const { timeout = 10000 } = options;
    
    // 如果已经在缓存中，直接返回
    if (this.has(url)) {
      return this.get(url);
    }

    // 如果正在加载，返回现有的Promise
    if (this.loadingPromises.has(url)) {
      return this.loadingPromises.get(url);
    }

    const loadPromise = this.loadImage(url, timeout);
    this.loadingPromises.set(url, loadPromise);

    try {
      const blob = await loadPromise;
      this.set(url, blob);
      return blob;
    } finally {
      this.loadingPromises.delete(url);
    }
  }

  // 加载图片
  async loadImage(url, timeout) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(url, {
        signal: controller.signal,
        mode: 'cors',
        cache: 'force-cache' // 强制使用浏览器缓存
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const blob = await response.blob();
      return blob;
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error('图片加载超时');
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  // 批量预加载
  async preloadBatch(urls, options = {}) {
    const { 
      batchSize = 3, 
      delay = 100,
      onProgress,
      onError 
    } = options;

    const results = [];
    
    for (let i = 0; i < urls.length; i += batchSize) {
      const batch = urls.slice(i, i + batchSize);
      
      const batchPromises = batch.map(async (url) => {
        try {
          const blob = await this.preload(url);
          return { url, blob, success: true };
        } catch (error) {
          if (onError) onError(url, error);
          return { url, error, success: false };
        }
      });

      const batchResults = await Promise.allSettled(batchPromises);
      results.push(...batchResults.map(r => r.value || r.reason));

      // 进度回调
      if (onProgress) {
        onProgress(Math.min(i + batchSize, urls.length), urls.length);
      }

      // 批次间延迟
      if (i + batchSize < urls.length && delay > 0) {
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }

    return results;
  }

  // 创建对象URL
  createObjectURL(blob) {
    return URL.createObjectURL(blob);
  }

  // 清理缓存
  clear() {
    this.cache.clear();
    this.loadingPromises.clear();
    this.cacheStats = { hits: 0, misses: 0, totalRequests: 0 };
  }

  // 获取缓存统计
  getStats() {
    const hitRate = this.cacheStats.totalRequests > 0 
      ? (this.cacheStats.hits / this.cacheStats.totalRequests * 100).toFixed(2)
      : 0;

    return {
      ...this.cacheStats,
      hitRate: `${hitRate}%`,
      cacheSize: this.cache.size,
      maxCacheSize: this.maxCacheSize
    };
  }
}

// 全局图片缓存实例
export const imageCache = new ImageCacheManager();

// 优化图片URL（针对缤纷云）
export const optimizeImageUrl = (url, options = {}) => {
  if (!url) return url;
  
  const { 
    width, 
    height, 
    quality = 80,
    format = 'webp'
  } = options;

  // 如果是缤纷云URL，添加优化参数
  if (url.includes('binfenyun.com') || url.includes('binfen')) {
    const separator = url.includes('?') ? '&' : '?';
    let params = [];
    
    if (width) params.push(`w=${width}`);
    if (height) params.push(`h=${height}`);
    if (quality) params.push(`q=${quality}`);
    if (format) params.push(`f=${format}`);
    
    if (params.length > 0) {
      return `${url}${separator}${params.join('&')}`;
    }
  }
  
  return url;
};

// 从数据中提取图片URL
export const extractImageUrls = (data) => {
  const urls = [];
  
  const extractFromObject = (obj) => {
    if (!obj || typeof obj !== 'object') return;
    
    for (const value of Object.values(obj)) {
      if (typeof value === 'string' && (
        value.includes('http') && (
          value.includes('.jpg') || 
          value.includes('.jpeg') || 
          value.includes('.png') || 
          value.includes('.webp') ||
          value.includes('binfenyun.com')
        )
      )) {
        urls.push(value);
      } else if (Array.isArray(value)) {
        value.forEach(extractFromObject);
      } else if (typeof value === 'object') {
        extractFromObject(value);
      }
    }
  };

  if (Array.isArray(data)) {
    data.forEach(extractFromObject);
  } else {
    extractFromObject(data);
  }

  return [...new Set(urls)]; // 去重
};

export default imageCache;